import {
  getWorkflowTaskIconFromType,
  getWorkflowTaskNameFromType,
  getWorkflowTriggerIconFromTrigger,
  getWorkflowTriggerNameFromTrigger,
} from "#src/components/Workflows/WorkflowsHelpers";
import { getDatasetSubmissionTriggerDatasetIds } from "#src/components/Workflows/WorkflowDatasetSubmissionTrigger.helpers";
import useLocalization from "#src/hooks/useLocalization";
import { IconVariants } from "@validereinc/common-components";
import {
  DatasetType,
  FormSchemaType,
  isTriggerAssetCreate,
  isTriggerAssetUpdate,
  isTriggerCron,
  isTriggerDatasetSubmission,
  isTriggerFormSubmissionCreate,
  isTriggerOtherWorkflowCompletion,
  SystemActionType,
  TypeOfObjectValues,
  UserTaskType,
  WorkflowConfigurationType,
  WorkflowStepType,
  WorkflowStepTypeType,
  WorkflowTemplateType,
  WorkflowTriggerDisplayType,
  WorkflowTriggerType,
} from "@validereinc/domain";
import { Workflows } from "@validereinc/domain-controllers";
import { cronToSimpleDisplayText } from "@validereinc/utilities";
import { Position } from "@xyflow/react";
import dagre from "dagre";
import ELK, { type ElkNode } from "elkjs/lib/elk.bundled";
import { isObject } from "es-toolkit/compat";
import { WorkflowGraphParallelNodeType } from "../ParallelGraphNode/types";
import { WorkflowGraphEdgeType } from "../WorkflowGraphEdge/types";
import { WorkflowGraphEdgeOffset } from "../WorkflowGraphEdge/WorkflowGraphEdge";
import { WorkflowGraphNodeType } from "../WorkflowGraphNode/types";

const { flattenWorkflowSteps } = Workflows.logic;

const elk = new ELK();
const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

export const getWorkflowConfigurationTriggerIndexFromTriggerKey = (
  triggerKey: string
): number => Number(triggerKey.replace("trigger-", ""));

export const getWorkflowConfigurationTriggerKeyFromIndex = (
  triggerIndex: number
): string => `trigger-${triggerIndex}`;

export const isWorkflowConfigurationTriggerKey = (nodeId: string): boolean =>
  nodeId.startsWith("trigger-");

const WORKFLOW_TRIGGER_ROW_SPACING = 80;
const WORKFLOW_TRIGGER_NODE_WIDTH = 296;

export const hasWorkflowNodePosition = (position: {
  x: number;
  y: number;
}): boolean => position.x !== 0 || position.y !== 0;

/**
 * Places a new trigger to the right of the rightmost sibling that already has
 * a canvas position. Returns undefined when dagre should lay out the full graph.
 */
export const getInitialPositionForNewWorkflowTriggerNode = <
  TNode extends { id: string; position: { x: number; y: number } },
>(
  triggerNodeId: string,
  nodes: TNode[]
): { x: number; y: number } | undefined => {
  const placedSiblingTriggers = nodes.filter(
    (node) =>
      isWorkflowConfigurationTriggerKey(node.id) &&
      node.id !== triggerNodeId &&
      hasWorkflowNodePosition(node.position)
  );

  if (placedSiblingTriggers.length === 0) {
    return undefined;
  }

  const rightmostSibling = placedSiblingTriggers.reduce((rightmost, node) =>
    node.position.x > rightmost.position.x ? node : rightmost
  );

  return {
    x:
      rightmostSibling.position.x +
      WORKFLOW_TRIGGER_NODE_WIDTH +
      WORKFLOW_TRIGGER_ROW_SPACING,
    y: rightmostSibling.position.y,
  };
};

/**
 * Merges stored canvas positions into generated nodes and assigns positions to
 * new trigger nodes so they do not stack on top of existing triggers.
 */
export const mergeWorkflowGraphNodePositions = <
  TNode extends { id: string; position: { x: number; y: number } },
>({
  generatedNodes,
  currentNodePositions,
}: {
  generatedNodes: TNode[];
  currentNodePositions: Map<string, { x: number; y: number }>;
}): TNode[] => {
  const nodesWithStoredPositions = generatedNodes.map((node) => {
    const currentPosition = currentNodePositions.get(node.id);

    if (currentPosition) {
      return {
        ...node,
        position: currentPosition,
      };
    }

    return node;
  });

  return nodesWithStoredPositions.map((node) => {
    if (
      !isWorkflowConfigurationTriggerKey(node.id) ||
      hasWorkflowNodePosition(node.position)
    ) {
      return node;
    }

    const initialPosition = getInitialPositionForNewWorkflowTriggerNode(
      node.id,
      nodesWithStoredPositions
    );

    if (!initialPosition) {
      return node;
    }

    return {
      ...node,
      position: initialPosition,
    };
  });
};

type RemapTriggerGraphLayoutAfterDeletionArgs<
  TNode extends { id: string; position: { x: number; y: number } },
> = {
  deleteIndex: number;
  nodes: TNode[];
  dirtyNodes: Record<string, boolean>;
};

/**
 * Reindexes canvas trigger node ids and dirty flags after a trigger is removed.
 * Survivors keep their positions but shift from `trigger-N` to `trigger-N-1` when
 * `N` is greater than the deleted index.
 */
export const remapTriggerGraphLayoutAfterDeletion = <
  TNode extends { id: string; position: { x: number; y: number } },
>({
  deleteIndex,
  nodes,
  dirtyNodes,
}: RemapTriggerGraphLayoutAfterDeletionArgs<TNode>): {
  nodes: TNode[];
  dirtyNodes: Record<string, boolean>;
} => {
  const deletedTriggerKey =
    getWorkflowConfigurationTriggerKeyFromIndex(deleteIndex);

  const remappedNodes = nodes
    .filter((node) => node.id !== deletedTriggerKey)
    .map((node) => {
      if (!isWorkflowConfigurationTriggerKey(node.id)) {
        return node;
      }

      const oldIndex = getWorkflowConfigurationTriggerIndexFromTriggerKey(
        node.id
      );
      const newIndex = oldIndex > deleteIndex ? oldIndex - 1 : oldIndex;
      const newId = getWorkflowConfigurationTriggerKeyFromIndex(newIndex);

      if (newId === node.id) {
        return node;
      }

      return {
        ...node,
        id: newId,
      };
    });

  const remappedDirtyNodes: Record<string, boolean> = {};

  Object.entries(dirtyNodes).forEach(([nodeId, isDirty]) => {
    if (!isDirty) {
      return;
    }

    if (!isWorkflowConfigurationTriggerKey(nodeId)) {
      remappedDirtyNodes[nodeId] = true;
      return;
    }

    const oldIndex = getWorkflowConfigurationTriggerIndexFromTriggerKey(nodeId);

    if (oldIndex === deleteIndex) {
      return;
    }

    const newIndex = oldIndex > deleteIndex ? oldIndex - 1 : oldIndex;
    remappedDirtyNodes[getWorkflowConfigurationTriggerKeyFromIndex(newIndex)] =
      true;
  });

  remappedNodes.forEach((node) => {
    if (isWorkflowConfigurationTriggerKey(node.id)) {
      remappedDirtyNodes[node.id] = true;
    }
  });

  remappedDirtyNodes[deletedTriggerKey] = true;

  return {
    nodes: remappedNodes,
    dirtyNodes: remappedDirtyNodes,
  };
};

export const getEstimatedNodeWidthAndHeight = (
  node: WorkflowGraphNodeType | WorkflowGraphParallelNodeType
): { width: number; height: number } => {
  if (node.data.variant === "finish")
    return { width: 180, height: 30 + WorkflowGraphEdgeOffset };
  return { width: 296, height: 110 + WorkflowGraphEdgeOffset };
};

/**
 * Performs automatic graph layout using either dagre or ELK (Eclipse Layout Kernel).
 *
 * - If ANY nodes have non-zero positions (from `visualization_metadata`), uses ELK
 *   to respect fixed positions while laying out other nodes relative to them.
 * - If ALL nodes have zero positions, uses dagre for simpler, faster layout.
 *
 * @param nodes - Array of workflow graph nodes to layout
 * @param edges - Array of workflow graph edges
 * @returns Promise resolving to layouted nodes and edges
 *
 * @example
 * // Nodes with visualization_metadata will have non-zero positions
 * const node = {
 *   id: "step-1",
 *   position: { x: 100, y: 200 }, // From visualization_metadata - will be preserved
 *   data: { ... }
 * };
 *
 * const { nodes: layouted } = await getLayoutedElements([node, ...otherNodes], edges);
 */
export const getLayoutedElements = async (
  nodes: Array<WorkflowGraphNodeType | WorkflowGraphParallelNodeType> = [],
  edges: WorkflowGraphEdgeType[] = []
): Promise<{
  nodes: Array<WorkflowGraphNodeType | WorkflowGraphParallelNodeType>;
  edges: WorkflowGraphEdgeType[];
}> => {
  // Check if any nodes have fixed positions (non-zero coordinates)
  const hasAnyFixedPositions = nodes.some(
    (node) => node.position.x !== 0 || node.position.y !== 0
  );

  // If no fixed positions, use dagre for simple layout
  if (!hasAnyFixedPositions) {
    return Promise.resolve(layoutWithDagre(nodes, edges));
  }

  // Otherwise, use ELK to handle fixed positions
  return layoutWithELK(nodes, edges);
};

/**
 * Layout nodes using dagre (simple tree layout, no fixed positions)
 */
const layoutWithDagre = (
  nodes: Array<WorkflowGraphNodeType | WorkflowGraphParallelNodeType>,
  edges: WorkflowGraphEdgeType[]
): {
  nodes: Array<WorkflowGraphNodeType | WorkflowGraphParallelNodeType>;
  edges: WorkflowGraphEdgeType[];
} => {
  dagreGraph.setGraph({ rankdir: "TB" });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, getEstimatedNodeWidthAndHeight(node));
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const newNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    const { width, height } = getEstimatedNodeWidthAndHeight(node);

    return {
      ...node,
      targetPosition: "top" as Position,
      sourcePosition: "bottom" as Position,
      // Dagre uses center positioning, shift to top-left for React Flow
      position: {
        x: nodeWithPosition.x - width / 2,
        y: nodeWithPosition.y - height / 2,
      },
    };
  });

  return { nodes: newNodes, edges };
};

/**
 * Layout nodes using ELK (supports fixed positions)
 */
const layoutWithELK = async (
  nodes: Array<WorkflowGraphNodeType | WorkflowGraphParallelNodeType>,
  edges: WorkflowGraphEdgeType[]
): Promise<{
  nodes: Array<WorkflowGraphNodeType | WorkflowGraphParallelNodeType>;
  edges: WorkflowGraphEdgeType[];
}> => {
  // Store original positions for nodes that have fixed positions
  const fixedNodePositions = new Map<
    string,
    { x: number; y: number; width: number; height: number }
  >();

  // Convert nodes to ELK format
  const elkNodes: ElkNode[] = nodes.map((node): ElkNode => {
    const { width, height } = getEstimatedNodeWidthAndHeight(node);
    const hasFixedPosition = node.position.x !== 0 || node.position.y !== 0;

    // Store fixed positions to restore them after ELK layout
    if (hasFixedPosition) {
      fixedNodePositions.set(node.id, {
        x: node.position.x,
        y: node.position.y,
        width,
        height,
      });
    }

    const elkNode: ElkNode = {
      id: node.id,
      width,
      height,
    };

    return elkNode;
  });

  // Convert edges to ELK format
  const elkEdges = edges.map((edge) => ({
    id: edge.id,
    sources: [edge.source],
    targets: [edge.target],
  }));

  // Create ELK graph
  const graph: ElkNode = {
    id: "root",
    layoutOptions: {
      "elk.algorithm": "layered",
      "elk.direction": "DOWN",
      "elk.spacing.nodeNode": "80",
      "elk.layered.spacing.nodeNodeBetweenLayers": "100",
      "elk.spacing.edgeNode": "40",
      "elk.spacing.edgeEdge": "20",
    },
    children: elkNodes,
    edges: elkEdges,
  };

  // Run ELK layout
  const layoutedGraph = await elk.layout(graph);

  // Create a map of ELK positions for easy lookup
  const elkPositions = new Map<string, { x: number; y: number }>();
  layoutedGraph.children?.forEach((elkNode) => {
    elkPositions.set(elkNode.id, { x: elkNode.x ?? 0, y: elkNode.y ?? 0 });
  });

  // Calculate offsets for each non-fixed node based on its parent fixed node
  const calculatePositionForNode = (
    nodeId: string
  ): { x: number; y: number } => {
    const fixedPos = fixedNodePositions.get(nodeId);
    if (fixedPos) {
      // This node has a fixed position, use it
      return { x: fixedPos.x, y: fixedPos.y };
    }

    // Find the parent/source node for this node
    const incomingEdge = edges.find((edge) => edge.target === nodeId);
    if (!incomingEdge) {
      // No parent, use ELK position
      return elkPositions.get(nodeId) ?? { x: 0, y: 0 };
    }

    // Get source node's final position (could be fixed or calculated)
    const sourceElkPos = elkPositions.get(incomingEdge.source);
    const sourceFinalPos = fixedNodePositions.get(incomingEdge.source);

    if (!sourceElkPos) {
      return elkPositions.get(nodeId) ?? { x: 0, y: 0 };
    }

    // If source has a fixed position, calculate offset
    if (sourceFinalPos) {
      const elkNodePos = elkPositions.get(nodeId);
      if (!elkNodePos) {
        return { x: 0, y: 0 };
      }

      // Calculate the offset from ELK layout between source and this node
      const elkOffsetX = elkNodePos.x - sourceElkPos.x;
      const elkOffsetY = elkNodePos.y - sourceElkPos.y;

      // Apply the same offset to the fixed source position
      return {
        x: sourceFinalPos.x + elkOffsetX,
        y: sourceFinalPos.y + elkOffsetY,
      };
    }

    // Source doesn't have fixed position, use ELK position
    return elkPositions.get(nodeId) ?? { x: 0, y: 0 };
  };

  // Convert back to React Flow format, restoring fixed positions and calculating relative positions
  const newNodes: Array<WorkflowGraphNodeType | WorkflowGraphParallelNodeType> =
    nodes.map((node) => {
      const finalPosition = calculatePositionForNode(node.id);

      return {
        ...node,
        targetPosition: "top" as Position,
        sourcePosition: "bottom" as Position,
        position: finalPosition,
      };
    });

  return { nodes: newNodes, edges };
};

export const workflowTemplateStepToGraphNode = (
  stepId: string,
  step: TypeOfObjectValues<WorkflowConfigurationType["steps"]>,
  { formSchemas: _ }: { formSchemas: Record<string, FormSchemaType> } = {
    formSchemas: {},
  }
): WorkflowGraphNodeType | WorkflowGraphParallelNodeType => {
  const stepTypeToVariantMap: Record<
    WorkflowStepTypeType,
    WorkflowGraphNodeType["data"]["variant"]
  > = {
    system_action: "system",
    user_task: "generic",
    parallel: "parallel",
  };

  // Use visualization_metadata if available, otherwise default to (0, 0)
  const position = step.visualization_metadata
    ? { x: step.visualization_metadata.x, y: step.visualization_metadata.y }
    : { x: 0, y: 0 };

  if (step.type === "parallel") {
    const nodeData: WorkflowGraphParallelNodeType = {
      id: stepId,
      position,
      type: "parallelNode",
      data: {
        variant: "parallel",
        title: step.name,
        icon: getWorkflowTaskIconFromType(step?.type),
        iconTitle: getWorkflowTaskNameFromType(step?.type),
        step,
      },
    };
    return nodeData;
  } else {
    const nodeData: WorkflowGraphNodeType = {
      id: stepId,
      position,
      type: "workflowNode",
      data: {
        icon: getWorkflowTaskIconFromType(step?.task.type),
        iconTitle: getWorkflowTaskNameFromType(step?.task.type),
        variant: stepTypeToVariantMap[step.type],
        title: step.name,
        description: "",
        step,
      },
    };

    if (step.description) {
      nodeData.data.description = step.description;
    }

    if (
      step.type === WorkflowStepType.SYSTEM_ACTION &&
      step.task.type === "delay"
    ) {
      const { duration, time_period } = step.task;
      // All available values for "time_period" look plural, and that's how it's defined in BE.
      // Remove last letter ("s") if duration = 1
      nodeData.data.title = `Wait for ${duration} ${duration === 1 ? time_period.slice(0, -1) : time_period}`;
    }

    return nodeData;
  }
};

export const workflowTemplateToTriggerNode = ({
  stepId,
  trigger,
  formSchemas,
  workflowTemplates,
  datasets,
  localize,
}: {
  stepId: string;
  trigger: WorkflowTriggerType;
  formSchemas: Record<string, FormSchemaType>;
  workflowTemplates: Record<string, WorkflowTemplateType>;
  datasets?: Record<string, DatasetType>;
  localize: ReturnType<typeof useLocalization>["localize"];
}): WorkflowGraphNodeType => {
  // Use visualization_metadata if available, otherwise default to (0, 0)
  const position = trigger.visualization_metadata
    ? {
        x: trigger.visualization_metadata.x,
        y: trigger.visualization_metadata.y,
      }
    : { x: 0, y: 0 };

  const node: WorkflowGraphNodeType = {
    id: stepId,
    position,
    type: "workflowNode",
    data: {
      icon: "cube",
      variant: "trigger",
      title: "Miscellaneous Trigger",
    },
  };

  if (trigger.type === WorkflowTriggerDisplayType.DUMMY) {
    node.data.title = "Add Trigger";
    node.data.isDummy = true;
    return node;
  }

  node.data.title = getWorkflowTriggerNameFromTrigger(trigger);
  node.data.icon = getWorkflowTriggerIconFromTrigger(trigger);

  if (isTriggerCron(trigger)) {
    node.data.title = "Scheduled";
    node.data.description = cronToSimpleDisplayText(trigger?.cron ?? "");
  }
  if (isTriggerFormSubmissionCreate(trigger)) {
    const associatedFormSchemaName =
      formSchemas?.[trigger.form_schema_id]?.name;
    if (associatedFormSchemaName) {
      node.data.title = `"${associatedFormSchemaName}" Submission`;
    }

    if (formSchemas?.[trigger.form_schema_id]?.form_category.name) {
      node.data.description =
        formSchemas[trigger.form_schema_id].form_category.name;
    }
  }
  if (isTriggerAssetCreate(trigger)) {
    node.data.title = `${localize(trigger.asset_type)} Creation`;
  }
  if (isTriggerAssetUpdate(trigger)) {
    node.data.title = `${localize(trigger.asset_type)} Update`;
  }
  if (isTriggerOtherWorkflowCompletion(trigger)) {
    const associatedWorkflowTemplateName =
      workflowTemplates?.[trigger.workflow_template_id]?.name;

    if (associatedWorkflowTemplateName) {
      node.data.title = `"${associatedWorkflowTemplateName}" Completion`;
    }

    if (
      workflowTemplates?.[trigger.workflow_template_id]?.workflow_category?.name
    ) {
      node.data.description =
        workflowTemplates[trigger.workflow_template_id].workflow_category.name;
    }
  }
  if (isTriggerDatasetSubmission(trigger)) {
    const datasetNames = getDatasetSubmissionTriggerDatasetIds(
      trigger.dataset_id
    )
      .map((datasetId) => datasets?.[datasetId]?.name)
      .filter((name): name is string => Boolean(name));

    if (datasetNames.length > 0) {
      node.data.description = datasetNames.join(", ");
    }
  }

  return node;
};

/**
 * Generate the suffix of the node id for the finish node
 * @param stepId - The id of the choice step
 * @param choiceParams - The parameters of the choice step
 * @param choiceParams.index - The index of the choice in the choices array
 * @returns The suffix of the node id
 */
const finishWorkflowNodeIdSuffix = (
  stepId: string,
  choiceParams?: { index: number }
): string => {
  // Use combination of step id and choice index as identifier since form choice and variable choice choices do not have an id field
  return choiceParams
    ? `${stepId}-choice-${choiceParams.index}-end`
    : `${stepId}-end`;
};

/**
 * Generate the config of the finish node
 * @param suffix - The suffix of the node id
 * @returns The config of the finish node
 */
const finishWorkflowNodeConfig = (suffix: string): WorkflowGraphNodeType => {
  return {
    id: `workflow-node-${suffix}`,
    position: { x: 0, y: 0 },
    type: "workflowNode",
    data: {
      icon: "seal-check",
      title: "Workflow Complete",
      variant: "finish",
    },
  };
};

/**
 * Outermost parallel in a branch whose finish node branch terminals should converge on.
 * Set when entering a parallel with `end` and not already inside another converging parallel.
 */
const getBranchConvergenceParallelId = (
  parallelStepId: string,
  parallelStep: TypeOfObjectValues<WorkflowConfigurationType["steps"]>,
  convergenceParallelId?: string
): string | undefined => {
  if (convergenceParallelId) {
    return convergenceParallelId;
  }

  if (parallelStep.end) {
    return parallelStepId;
  }

  return undefined;
};

const getParallelBranchFinishNodeTargetId = (
  parentParallelId: string,
  convergenceParallelId?: string
): string => {
  return convergenceParallelId ?? parentParallelId;
};

/**
 * Whether to emit `workflow-node-${stepId}-end` (see workflowTemplateToEdges).
 * Top-level non-parallel steps with `end` always get a finish edge, even when `next` is set.
 * Nested parallels only get their own finish node when they are the convergence point.
 */
const shouldEmitWorkflowStepFinishNode = (
  stepId: string,
  stepDefinition: TypeOfObjectValues<WorkflowConfigurationType["steps"]>,
  parentId: string,
  inheritedNext?: string,
  convergenceParallelId?: string
): boolean => {
  if (!stepDefinition.end) {
    return false;
  }

  if (!parentId) {
    if (stepDefinition.type === WorkflowStepType.PARALLEL) {
      return !(stepDefinition.next ?? inheritedNext);
    }

    return true;
  }

  if (stepDefinition.type !== WorkflowStepType.PARALLEL) {
    return false;
  }

  if (stepDefinition.next ?? inheritedNext) {
    return false;
  }

  return !convergenceParallelId || convergenceParallelId === stepId;
};

export const workflowTemplateToNodes = (
  {
    template,
    formSchemas,
    localize,
    workflowTemplates,
    datasets,
  }: {
    template: {
      config?: WorkflowConfigurationType;
      triggers?: WorkflowTriggerType[];
    };
    formSchemas: Record<string, FormSchemaType>;
    workflowTemplates: Record<string, WorkflowTemplateType>;
    datasets?: Record<string, DatasetType>;
    localize: ReturnType<typeof useLocalization>["localize"];
  },
  nodes: Array<WorkflowGraphNodeType | WorkflowGraphParallelNodeType> = [],
  parentId = "",
  inheritedNext?: string,
  convergenceParallelId?: string
): Array<WorkflowGraphNodeType | WorkflowGraphParallelNodeType> => {
  // Add trigger nodes
  if (template.triggers)
    template.triggers.forEach((trigger, index) => {
      const triggerNode = workflowTemplateToTriggerNode({
        stepId: getWorkflowConfigurationTriggerKeyFromIndex(index),
        trigger,
        formSchemas,
        workflowTemplates,
        datasets,
        localize,
      });
      nodes.push(triggerNode);
    });

  if (template.config)
    Object.entries(template.config.steps).forEach(
      ([stepId, stepDefinition]) => {
        // Add step nodes
        const node = workflowTemplateStepToGraphNode(stepId, stepDefinition, {
          formSchemas,
        });
        nodes.push(node);

        if (stepDefinition.type === WorkflowStepType.PARALLEL) {
          const branchInheritedNext = stepDefinition.next ?? inheritedNext;
          const branchConvergenceParallelId = getBranchConvergenceParallelId(
            stepId,
            stepDefinition,
            convergenceParallelId
          );
          stepDefinition.branches.forEach((branch) => {
            workflowTemplateToNodes(
              {
                formSchemas: formSchemas,
                localize,
                workflowTemplates,
                datasets,
                template: {
                  config: branch,
                  triggers: [],
                },
              },
              nodes,
              stepId,
              branchInheritedNext,
              branchConvergenceParallelId
            );
          });
        }

        if (
          shouldEmitWorkflowStepFinishNode(
            stepId,
            stepDefinition,
            parentId,
            inheritedNext,
            convergenceParallelId
          )
        ) {
          const suffix = finishWorkflowNodeIdSuffix(stepId);
          const finishNodeId = `workflow-node-${suffix}`;
          if (!nodes.some((node) => node.id === finishNodeId)) {
            nodes.push(finishWorkflowNodeConfig(suffix));
          }
        }

        // Add finish nodes for choices (which may end without requiring an explicit step)
        if (
          (stepDefinition.type === WorkflowStepType.USER_TASK &&
            stepDefinition.task.type === UserTaskType.CHOICE) ||
          (stepDefinition.type === WorkflowStepType.SYSTEM_ACTION &&
            (stepDefinition.task.type === SystemActionType.FORM_CHOICE ||
              stepDefinition.task.type === SystemActionType.VARIABLE_CHOICE))
        ) {
          // Only create end nodes for choices not in parallel branches
          if (!parentId) {
            stepDefinition.task.choices.forEach((choice, index) => {
              if (choice.end) {
                const suffix = finishWorkflowNodeIdSuffix(stepId, { index });
                nodes.push(finishWorkflowNodeConfig(suffix));
              }
            });
          }
        }
      }
    );

  return nodes;
};

export const workflowTemplateToEdges = (
  template: {
    config?: WorkflowConfigurationType;
    triggers?: WorkflowTriggerType[];
    next?: string;
    end?: boolean;
  },
  { formSchemas: _ }: { formSchemas: Record<string, FormSchemaType> },
  finalEdges: WorkflowGraphEdgeType[] = [],
  parentId = "",
  convergenceParallelId?: string
): WorkflowGraphEdgeType[] => {
  if (!template.config || !template.triggers) return finalEdges;

  Object.entries(template.config.steps).forEach(([stepId, step]) => {
    if (step.type === WorkflowStepType.PARALLEL) {
      const branchConvergenceParallelId = getBranchConvergenceParallelId(
        stepId,
        step,
        convergenceParallelId
      );
      step.branches.forEach((branch) => {
        // If the parallel step ends but has no next, use the parent's next
        // so that nested steps can point to the parent's next step
        const nextStep = step.next ?? template.next;
        workflowTemplateToEdges(
          {
            config: branch,
            triggers: [],
            next: nextStep,
            end: step.end,
          },
          { formSchemas: _ },
          finalEdges,
          stepId,
          branchConvergenceParallelId
        );
      });
    }

    // Edges from triggers to start node
    if (stepId === template.config?.start) {
      if (!parentId) {
        template.triggers?.forEach((_, index) => {
          finalEdges.push({
            id: `trigger-edge-${index}-${stepId}`,
            source: getWorkflowConfigurationTriggerKeyFromIndex(index),
            target: stepId,
            type: "workflowEdge",
            data: {},
          });
        });
        //edges from parent parallel step to first inner child
      } else if (parentId) {
        finalEdges.push({
          id: `${parentId}-${stepId}`,
          source: parentId,
          target: stepId,
          type: "workflowEdge",
          data: {},
        });
      }
    }

    // Handling multi-way edges:
    if (
      step.type !== WorkflowStepType.PARALLEL &&
      (step.task.type === "form_choice" ||
        step.task.type === "choice" ||
        step.task.type === "variable_choice")
    ) {
      const choices = step.task.choices;

      const uniqueChoices = Array.from(
        new Set(choices.map((c) => JSON.stringify(c)))
      ).map((c) => JSON.parse(c) as (typeof step.task.choices)[0]);

      uniqueChoices.forEach((choice, index) => {
        let label = "";
        let icon: IconVariants | undefined = undefined;

        if (step.task.type === "form_choice") {
          const answer_filter = (choice as (typeof step.task.choices)[0])
            .answer_filter;

          icon = "clipboard-check";
          if (answer_filter["answer.value"]?.$in) {
            label = (answer_filter["answer.value"].$in as string[]).join(", ");
          } else if (isObject(answer_filter["answer.value"])) {
            label = JSON.stringify(answer_filter["answer.value"]);
          } else {
            label = answer_filter["answer.value"];
          }
        }

        if (step.task.type === "variable_choice") {
          label = `Path ${index + 1}`;
          icon = "bezier-curve";
        }

        if (step.task.type === "choice") {
          label = step.task.choices[index].name;
          icon = "check-square-offset";
        }

        if (choice?.next) {
          finalEdges.push({
            id: `${stepId}-${choice?.next ? choice.next : "end"}-${index}`,
            source: stepId,
            target: choice.next,
            type: "workflowEdge",
            data: { label, icon },
          });
        }

        if (choice?.end && parentId && template.next) {
          finalEdges.push({
            id: `${stepId}-end-${index}`,
            source: stepId,
            target: template.next,
            type: "workflowEdge",
            data: { label, icon },
          });
        } else if (choice?.end && parentId && template.end) {
          const finishTargetId = getParallelBranchFinishNodeTargetId(
            parentId,
            convergenceParallelId
          );
          finalEdges.push({
            id: `${stepId}-end-${index}`,
            source: stepId,
            target: `workflow-node-${finishTargetId}-end`,
            type: "workflowEdge",
            data: { label, icon },
          });
          // Do not generate for choice steps in nested inside parallel steps
        } else if (choice?.end && !parentId) {
          const suffix = finishWorkflowNodeIdSuffix(stepId, { index });
          finalEdges.push({
            id: suffix,
            source: stepId,
            target: `workflow-node-${suffix}`,
            type: "workflowEdge",
            data: { label, icon },
          });
        }
      });

      if (
        !finalEdges.find((e) => e.id.startsWith(`${stepId}-${step.next}-`)) &&
        step.next
      ) {
        finalEdges.push({
          id: `${stepId}-${step.next}`,
          source: stepId,
          target: step.next,
          type: "workflowEdge",
          data: { label: "Default answer" },
        });
      } else {
        const find = finalEdges.findIndex((e) =>
          e.id.startsWith(`${stepId}-${step.next}-`)
        );
        if (finalEdges[find]?.data)
          finalEdges[find].data.label += " & Other answers";
      }
    } else if (
      step.type !== WorkflowStepType.PARALLEL &&
      step.task.type === "assess_templated_report_for_comments"
    ) {
      finalEdges.push({
        id: `${stepId}-${step.task.assessment_success_next}`,
        source: stepId,
        target: step.task.assessment_success_next,
        type: "workflowEdge",
        data: { label: "Success", icon: "check-circle" },
      });
      finalEdges.push({
        id: `${stepId}-${step.task.assessment_failure_next}`,
        source: stepId,
        target: step.task.assessment_failure_next,
        type: "workflowEdge",
        data: { label: "Failure", icon: "x-circle" },
      });
    } else {
      // add next step unless the current step is a parallel step
      if (step.next && step.type !== WorkflowStepType.PARALLEL) {
        finalEdges.push({
          id: `${stepId}-${step.next}`,
          source: stepId,
          target: step.next,
          type: "workflowEdge",
          data: {},
        });
      }
    }

    // Edges end nodes to their associated "Finish" nodes
    if (step.end) {
      if (!parentId && step.type !== WorkflowStepType.PARALLEL) {
        finalEdges.push({
          id: `workflow-edge-${stepId}-to-end`,
          source: stepId,
          target: `workflow-node-${stepId}-end`,
          type: "workflowEdge",
          data: {},
        });

        //edges from inner child parallel step to parent's next step
      } else if (parentId && template.next) {
        finalEdges.push({
          id: `${stepId}-${template.next}`,
          source: stepId,
          target: template.next,
          type: "workflowEdge",
          data: {},
        });
        //last inner parallel tasks to end of workflow
      } else if (parentId && template.end) {
        const finishTargetId = getParallelBranchFinishNodeTargetId(
          parentId,
          convergenceParallelId
        );
        finalEdges.push({
          id: `workflow-edge-${stepId}-${finishTargetId}-to-end`,
          source: stepId,
          target: `workflow-node-${finishTargetId}-end`,
          type: "workflowEdge",
          data: {},
        });
      }
    }
  });

  return finalEdges;
};

export const extractFormSchemaIdsFromWorkflowTemplate = ({
  triggers,
  config,
}: {
  config?: WorkflowConfigurationType;
  triggers?: WorkflowTriggerType[];
}): string[] => {
  const neededFormSchemas: string[] = [];

  if (config)
    Object.values(flattenWorkflowSteps(config.steps)).map((step) => {
      if (
        (step.task.type === "submit_form" ||
          step.task.type === "form_choice") &&
        step.task.form_schema_id
      ) {
        neededFormSchemas.push(step.task.form_schema_id);
      }
    });

  if (triggers)
    triggers.forEach((trigger) => {
      if (isTriggerFormSubmissionCreate(trigger) && trigger.form_schema_id) {
        neededFormSchemas.push(trigger.form_schema_id);
      }
    });

  return neededFormSchemas;
};

export const extractWorkflowTemplateIdsFromWorkflowTemplate = ({
  triggers,
}: {
  triggers?: WorkflowTriggerType[];
}): string[] => {
  const neededWorkflowTemplates: string[] = [];

  if (triggers)
    triggers.forEach((trigger) => {
      if (
        isTriggerOtherWorkflowCompletion(trigger) &&
        trigger.workflow_template_id
      ) {
        neededWorkflowTemplates.push(trigger.workflow_template_id);
      }
    });

  return neededWorkflowTemplates;
};

export const extractDatasetIdsFromWorkflowTemplate = ({
  triggers,
}: {
  triggers?: WorkflowTriggerType[];
}): string[] => {
  const neededDatasetIds: string[] = [];

  if (triggers) {
    triggers.forEach((trigger) => {
      if (isTriggerDatasetSubmission(trigger)) {
        neededDatasetIds.push(
          ...getDatasetSubmissionTriggerDatasetIds(trigger.dataset_id)
        );
      }
    });
  }

  return neededDatasetIds;
};

/**
 * Maps all input and output variables for each step in a workflow configuration.
 *
 * @param {WorkflowConfigurationType} config - The workflow configuration containing steps to process
 * @returns {Record<string, { input: string[]; output: string[] }>} An object mapping step IDs to their input and output variables
 */
export const mapTaskVariables = (config: WorkflowConfigurationType) => {
  const variables: Record<string, { input: string[]; output: string[] }> = {};
  Object.entries(flattenWorkflowSteps(config.steps)).forEach(
    ([stepId, step]) => {
      variables[stepId] = { input: [], output: [] };

      if (step.task.input) {
        Object.values(step.task.input).forEach((val) => {
          variables[stepId].input.push(val);
        });
      }
      if (step.task.output) {
        Object.keys(step.task.output).forEach((key) => {
          variables[stepId].output.push(key);
        });
      }
    }
  );
  return variables;
};

/**
 * Extracts all output variables from workflow triggers.
 *
 * @param {WorkflowTriggerType[]} triggers - Array of workflow triggers to process
 * @returns {string[]} Array of unique variable names defined in trigger outputs
 */
export const mapTriggerVariables = (triggers: WorkflowTriggerType[]) =>
  Array.from(
    triggers.reduce((acc, trigger) => {
      if (trigger.output) {
        Object.keys(trigger.output).forEach((key) => {
          acc.add(key);
        });
      }
      return acc;
    }, new Set<string>())
  );

/**
 * Extracts all output variables from a workflow template.
 *
 * @param {WorkflowTemplateType} template - The workflow template to analyze
 * @returns {string[]} Array of unique variable names defined in the template's output
 */
export const mapTemplateVariables = (template: WorkflowTemplateType) =>
  Array.from(
    Object.keys(template.output).reduce((acc, key) => {
      acc.add(key);
      return acc;
    }, new Set<string>())
  );
/**
 * Extracts all initial variable names from a workflow template.
 *
 * @param {WorkflowTemplateType} template - The workflow template to analyze
 * @returns {string[]} Array of unique variable names defined in the template's initial_variables
 */
export const mapInitialVariables = (template: WorkflowTemplateType) =>
  Array.from(
    Object.keys(template.initial_variables ?? {}).reduce((acc, key) => {
      acc.add(key);
      return acc;
    }, new Set<string>())
  );
/**
 * Creates a mapping of workflow steps to their possible next steps (forward path map).
 * Analyzes all possible paths in the workflow, including conditional branches.
 *
 * @param {WorkflowConfigurationType} config - The workflow configuration to process
 * @returns {Record<string, string[]>} An object mapping each step ID to an array of its possible next step IDs
 */
export const getPathMap = (config: WorkflowConfigurationType) => {
  const pathMap: Record<string, string[]> = {};
  //TODO: this needs to be updated to support parallel tasks
  Object.entries(config.steps).forEach(([stepId, step]) => {
    if (!pathMap[stepId]) {
      pathMap[stepId] = [];
    }
    if (step.next) {
      pathMap[stepId].push(step.next);
    }
    if (step.type === WorkflowStepType.SYSTEM_ACTION) {
      if (
        step.task.type === SystemActionType.ASSESS_TEMPLATED_REPORT_FOR_COMMENTS
      ) {
        if (step.task.assessment_success_next) {
          pathMap[stepId].push(step.task.assessment_success_next);
        }
        if (step.task.assessment_failure_next) {
          pathMap[stepId].push(step.task.assessment_failure_next);
        }
      }
      if (step.task.type === SystemActionType.FORM_CHOICE) {
        step.task.choices.forEach((choice) => {
          if (choice.next) {
            pathMap[stepId].push(choice.next);
          }
        });
      }
    }
    if (step.type === WorkflowStepType.USER_TASK) {
      if (step.task.type === UserTaskType.CHOICE) {
        step.task.choices.forEach((choice) => {
          if (choice.next) {
            pathMap[stepId].push(choice.next);
          }
        });
      }
    }
  });
  return pathMap;
};

/**
 * Creates a reverse mapping of workflow steps to their possible parent steps.
 *
 * @param {Record<string, string[]>} pathMap - The forward path map generated by getPathMap
 * @returns {Record<string, string[]>} An object mapping each step ID to an array of its parent step IDs
 */
export const getReversePathMap = (
  pathMap: Record<string, string[]>
): Record<string, string[]> => {
  const reverseMap: Record<string, string[]> = {};

  for (const [from, toList] of Object.entries(pathMap)) {
    for (const to of toList) {
      if (!reverseMap[to]) reverseMap[to] = [];
      reverseMap[to].push(from);
    }
  }

  return reverseMap;
};

/**
 * Helper function that performs a depth-first search to find all ancestors of a given step.
 * Looks through the possible parents of a starting step ID and recursively finds all its possible ancestors.
 *
 * @param {string} current - The current step ID being processed
 * @param {Record<string, string[]>} reversePathMap - The reverse path map generated by getReversePathMap
 * @param {Set<string>} ancestors - A set to collect all ancestor step IDs
 * @param {Set<string>} visited - A set to track visited step IDs and prevent infinite loops
 */
function traverseParents(
  current: string,
  reversePathMap: Record<string, string[]>,
  ancestors: Set<string>,
  visited: Set<string>
) {
  if (!reversePathMap[current]) return;
  for (const parent of reversePathMap[current]) {
    if (!visited.has(parent)) {
      visited.add(parent);
      ancestors.add(parent);
      traverseParents(parent, reversePathMap, ancestors, visited);
    }
  }
}

/**
 * Collects all pssible parent (ancestor) steps for a given step in the workflow.
 *
 * @param {string} step - The step ID to find ancestors for
 * @param {Record<string, string[]>} reversePathMap - The reverse path map generated by getReversePathMap
 * @param {Set<string>} [visited=new Set<string>()] - A set to track visited step IDs and prevent cycles
 * @returns {string[]} An array of unique step IDs that are ancestors of the given step
 */
export const collectParentPathsForStep = (
  step: string,
  reversePathMap: Record<string, string[]>,
  visited = new Set<string>()
): string[] => {
  const ancestors = new Set<string>();
  traverseParents(step, reversePathMap, ancestors, visited);
  return Array.from(ancestors).filter((ancestor) => ancestor !== step);
};

/**
 * Determines all available variables that can be used by a given step.
 * Variables can come from template, triggers, initial variables, or parent steps.
 *
 * @param {string[]} parentSteps - Array of parent step IDs
 * @param {string[]} templateVariables - Array of variables defined in the workflow template, from mapTemplateVariables
 * @param {Record<string, { input: string[]; output: string[] }>} taskVariables - Mapping of task variables from mapTaskVariables
 * @param {string[]} triggerVariables - Array of variables defined by workflow triggers, from mapTriggerVariables
 * @param {string[]} initialVariables - Array of variables defined in the workflow template's initial_variables, from mapInitialVariables
 * @returns {string[]} Array of unique variable names available to the provided step
 */
export const getAvailableVariablesForStep = (
  parentSteps: string[],
  templateVariables: string[],
  taskVariables: Record<string, { input: string[]; output: string[] }> = {},
  triggerVariables: string[],
  initialVariables: string[] = []
) => {
  const variables = [
    ...templateVariables,
    ...triggerVariables,
    ...initialVariables,
  ];
  parentSteps.forEach((parent) => {
    if (taskVariables[parent]?.output)
      variables.push(...taskVariables[parent].output);
  });
  return Array.from(new Set(variables));
};

export type WorkflowGraphQueryLoadingStateType = {
  templateId?: string;
  isLoadingTemplate: boolean;
  hasWorkflowTemplateData: boolean;
  formSchemaIdsToFetch: readonly string[];
  isLoadingFormSchemas: boolean;
  hasFormSchemasData: boolean;
  workflowTemplateIdsToFetch: readonly string[];
  isLoadingWorkflowTemplates: boolean;
  hasWorkflowTemplatesData: boolean;
  datasetIdsToFetch: readonly string[];
  isLoadingDatasets: boolean;
  hasDatasetsData: boolean;
};

/** True only while required queries have no cached data yet (not background refetches). */
export const isWorkflowGraphInitialLoading = (
  state: WorkflowGraphQueryLoadingStateType
): boolean => {
  const isLoadingTemplateWithoutData =
    Boolean(state.templateId) &&
    state.isLoadingTemplate &&
    !state.hasWorkflowTemplateData;

  const isLoadingFormSchemasWithoutData =
    state.formSchemaIdsToFetch.length > 0 &&
    state.isLoadingFormSchemas &&
    !state.hasFormSchemasData;

  const isLoadingWorkflowTemplatesWithoutData =
    state.workflowTemplateIdsToFetch.length > 0 &&
    state.isLoadingWorkflowTemplates &&
    !state.hasWorkflowTemplatesData;

  const isLoadingDatasetsWithoutData =
    state.datasetIdsToFetch.length > 0 &&
    state.isLoadingDatasets &&
    !state.hasDatasetsData;

  return (
    isLoadingTemplateWithoutData ||
    isLoadingFormSchemasWithoutData ||
    isLoadingWorkflowTemplatesWithoutData ||
    isLoadingDatasetsWithoutData
  );
};

export const shouldClearWorkflowGraphNodesAndEdges = ({
  isLoading,
  hasRenderedGraphNodes,
  hasStaleGraphNodes,
}: {
  isLoading: boolean;
  hasRenderedGraphNodes: boolean;
  hasStaleGraphNodes: boolean;
}) => isLoading && (!hasRenderedGraphNodes || hasStaleGraphNodes);
